import {
  X,
  Pencil,
  Printer,
  CheckCircle,
  Clock,
  Calendar,
  Wallet,
  Tags,
  Truck,
  CreditCard,
  FileText,
  Hash,
  User,
  Paperclip,
  RefreshCw,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import type { Expense } from '../../../types/expense';

interface ExpenseDetailsModalProps {
  open: boolean;
  onClose: () => void;
  expense: Expense | null;
  onEdit: (expense: Expense) => void;
  onPrint?: (expense: Expense) => void;
}

const CURRENCY = '৳';

const FREQUENCY_LABELS: Record<string, string> = {
  DAILY: 'Daily',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  YEARLY: 'Yearly',
};

function fmtMoney(value: number | undefined | null): string {
  return `${CURRENCY}${(Number(value) || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function fmtDate(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function fmtDateTime(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function DetailItem({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-xs font-medium text-text-muted uppercase mb-1 flex items-center gap-1.5">
        <Icon size={12} />
        {label}
      </p>
      <p className="text-sm text-text break-words">{value}</p>
    </div>
  );
}

export function ExpenseDetailsModal({ open, onClose, expense, onEdit, onPrint }: ExpenseDetailsModalProps) {
  if (!open || !expense) return null;

  const isPaid = expense.status === 'PAID';
  const isRecurring = expense.expenseType === 'RECURRING';
  const payee = expense.vendor?.name || expense.vendorName;
  const enteredByName =
    expense.enteredBy
      ? `${expense.enteredBy.firstName || ''} ${expense.enteredBy.lastName || ''}`.trim() ||
        expense.enteredBy.name
      : '';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-border bg-background/50 rounded-t-2xl">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <Wallet size={20} className="text-primary" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-text truncate">{expense.title}</h2>
              <p className="text-xs text-text-muted font-mono">{expense.expenseNumber}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-background rounded-xl transition-colors"
          >
            <X size={18} className="text-text-muted" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Amount + status */}
          <div className="flex items-center justify-between gap-4 rounded-xl border border-border bg-background/50 p-4">
            <div>
              <p className="text-xs font-medium text-text-muted uppercase">Amount</p>
              <p className="text-2xl font-bold text-text mt-1">{fmtMoney(expense.amount)}</p>
            </div>
            <span
              className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                isPaid ? 'bg-success/10 text-success' : 'bg-warning/10 text-warning'
              }`}
            >
              {isPaid ? <CheckCircle size={14} /> : <Clock size={14} />}
              {isPaid ? 'Paid' : 'Pending'}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <DetailItem icon={Tags} label="Category" value={expense.category?.name || 'Uncategorised'} />
            <DetailItem icon={Truck} label="Payee / Vendor" value={payee || '—'} />
            <DetailItem icon={Calendar} label="Expense Date" value={fmtDate(expense.expenseDate)} />
            <DetailItem
              icon={CreditCard}
              label="Payment Method"
              value={(expense.paymentMethod || 'cash').replace(/_/g, ' ').toUpperCase()}
            />
            <DetailItem icon={Hash} label="Reference" value={expense.paymentReference || '—'} />
            <DetailItem
              icon={RefreshCw}
              label="Type"
              value={
                isRecurring
                  ? `Recurring · ${FREQUENCY_LABELS[expense.frequency || ''] || expense.frequency || ''}`
                  : 'One-time'
              }
            />
            <DetailItem icon={Calendar} label="Paid On" value={isPaid ? fmtDate(expense.paidAt) : '—'} />
            <DetailItem icon={User} label="Recorded By" value={enteredByName || '—'} />
            {expense.attachmentUrl && (
              <DetailItem
                icon={Paperclip}
                label="Attachment"
                value={
                  <a
                    href={expense.attachmentUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary hover:underline break-all"
                  >
                    View document
                  </a>
                }
              />
            )}
          </div>

          {expense.description && (
            <div className="pt-4 border-t border-border">
              <p className="text-xs font-medium text-text-muted uppercase mb-1 flex items-center gap-1.5">
                <FileText size={12} />
                Description
              </p>
              <p className="text-sm text-text whitespace-pre-wrap">{expense.description}</p>
            </div>
          )}

          <div className="pt-4 border-t border-border">
            <p className="text-xs font-medium text-text-muted uppercase mb-1 flex items-center gap-1.5">
              <FileText size={12} />
              Notes
            </p>
            <p className="text-sm text-text whitespace-pre-wrap">{expense.notes || 'No notes provided.'}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t border-border">
            <DetailItem icon={Calendar} label="Created" value={fmtDateTime(expense.createdAt)} />
            <DetailItem icon={Calendar} label="Last updated" value={fmtDateTime(expense.updatedAt)} />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-border">
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
          {onPrint && (
            <Button type="button" variant="outline" onClick={() => onPrint(expense)}>
              <Printer size={16} className="mr-2" />
              Print Voucher
            </Button>
          )}
          <Button type="button" onClick={() => onEdit(expense)}>
            <Pencil size={16} className="mr-2" />
            Edit
          </Button>
        </div>
      </div>
    </div>
  );
}
